/**
 * TeamManagementPage — Menaxhimi i Ekipit (supervisor → gjithë ekipi)
 * Route: /team-management
 *
 * Faqja: listë kompakte e menaxherëve. Klikimi mbi një menaxher hap një dialog
 * profesional me TABELË të ekipit (të gjithë nën menaxhim — direkt e indirekt),
 * me kërkim, detaje (punësuar/mbaron), nivele dhe drill-down hierarkik.
 */
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Box, Card, CardContent, Paper, Typography, Stack, Avatar, AvatarGroup, Chip,
  IconButton, TextField, InputAdornment, Skeleton, Divider, Tooltip, Grid,
  Dialog, DialogContent, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import SearchIcon from '@mui/icons-material/Search';
import SupervisorAccountOutlinedIcon from '@mui/icons-material/SupervisorAccountOutlined';
import GroupsOutlinedIcon from '@mui/icons-material/GroupsOutlined';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import KeyboardArrowRightRoundedIcon from '@mui/icons-material/KeyboardArrowRightRounded';
import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import AccountTreeOutlinedIcon from '@mui/icons-material/AccountTreeOutlined';
import PeopleAltOutlinedIcon from '@mui/icons-material/PeopleAltOutlined';
import PageHeader from '../components/common/PageHeader';
import apiService from '../services/apiServices';

/* ─── helpers ─────────────────────────────────────────────────────────── */
const AVATAR_PALETTE = [
  ['#1D4ED8', '#DBEAFE'], ['#6D28D9', '#EDE9FE'], ['#065F46', '#D1FAE5'],
  ['#92400E', '#FEF3C7'], ['#9F1239', '#FFE4E6'], ['#0E7490', '#CFFAFE'],
  ['#4338CA', '#E0E7FF'], ['#166534', '#DCFCE7'],
];
const avatarColors = (name) => {
  if (!name || name === '—') return ['#374151', '#F3F4F6'];
  let h = 0;
  for (let i = 0; i < name.length; i++) h = (h * 31 + name.charCodeAt(i)) & 0xffffffff;
  return AVATAR_PALETTE[Math.abs(h) % AVATAR_PALETTE.length];
};
const initialsOf = (name) =>
  (name && name !== '—')
    ? name.split(' ').filter(Boolean).map(x => x[0]).join('').slice(0, 2).toUpperCase()
    : '?';
const nameOf = (w) =>
  (w.FullName || `${w.FirstName || w.first_name || ''} ${w.LastName || w.last_name || ''}`.trim()) || '—';
const positionOf = (w) => w.Position || w.position || '';
const departmentOf = (w) => w.DepartmentName || w.department_name || '';
const idOf = (w) => String(w.Id ?? w.id);
const hireOf = (w) => w.HireDate || w.hire_date || w.StartDate || w.start_date || '';
const endOf  = (w) => w.EmploymentEndDate || w.employment_end_date || '';
const fmtD = (v) => {
  if (!v) return '';
  const d = new Date(v);
  if (isNaN(d.getTime())) return String(v).slice(0, 10);
  const p = (n) => String(n).padStart(2, '0');
  return `${p(d.getDate())}.${p(d.getMonth() + 1)}.${d.getFullYear()}`;
};

/* ─── KPI stat card ────────────────────────────────────────────────────── */
function StatCard({ icon: Icon, iconBg, iconColor, value, label, loading }) {
  return (
    <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, height: '100%' }}>
      <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Box>
            <Typography sx={{ color: 'text.secondary', fontWeight: 600, letterSpacing: 0.4, textTransform: 'uppercase', fontSize: '0.64rem' }}>
              {label}
            </Typography>
            {loading
              ? <Skeleton width={48} height={30} sx={{ mt: 0.2 }} />
              : <Typography sx={{ fontWeight: 800, fontSize: '1.35rem', lineHeight: 1.1, mt: 0.2 }}>{value}</Typography>
            }
          </Box>
          <Avatar sx={{ bgcolor: iconBg, color: iconColor, width: 38, height: 38 }}>
            <Icon fontSize="small" />
          </Avatar>
        </Stack>
      </CardContent>
    </Card>
  );
}

/* ─── main component ───────────────────────────────────────────────────── */
export default function TeamManagementPage() {
  const { t }  = useTranslation();
  const theme  = useTheme();
  const [workers,  setWorkers]  = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [error,    setError]    = useState('');
  const [search,   setSearch]   = useState('');
  const [path,     setPath]     = useState([]);      // breadcrumb of drilled managers
  const [teamSearch, setTeamSearch] = useState('');  // search inside the dialog

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await apiService().query('selectworker', '');
        if (cancelled) return;
        if (res?.success && Array.isArray(res.data)) setWorkers(res.data);
        else setError(t('teamManagement.loadError', 'Ngarkimi dështoi'));
      } catch (e) {
        if (!cancelled) setError(e.message || t('teamManagement.loadErrorGeneric', 'Ndodhi një gabim'));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [t]);

  const byId = useMemo(() => {
    const m = {};
    workers.forEach(w => { m[idOf(w)] = w; });
    return m;
  }, [workers]);

  const childrenMap = useMemo(() => {
    const m = {};
    workers.forEach(w => {
      const sup = w.SupervisorId ?? w.supervisor_id;
      if (sup != null && String(sup) !== '' && String(sup) !== idOf(w)) {
        (m[String(sup)] ||= []).push(w);
      }
    });
    return m;
  }, [workers]);

  const subtreeOf = useCallback((supId) => {
    const out = [];
    const seen = new Set([String(supId)]);
    const walk = (id, level) => {
      const kids = (childrenMap[String(id)] || []).slice().sort((a, b) => nameOf(a).localeCompare(nameOf(b)));
      kids.forEach(k => {
        const kid = idOf(k);
        if (seen.has(kid)) return;
        seen.add(kid);
        out.push({ worker: k, level });
        walk(kid, level + 1);
      });
    };
    walk(supId, 1);
    return out;
  }, [childrenMap]);

  const teams = useMemo(() => {
    return Object.keys(childrenMap)
      .map(supId => {
        const managed = subtreeOf(supId);
        return {
          supervisor: byId[supId] || { Id: supId, FullName: t('teamManagement.unknownSupervisor', 'I panjohur') },
          direct: childrenMap[supId].length,
          managed,
          levels: managed.reduce((mx, m) => Math.max(mx, m.level), 0),
        };
      })
      .sort((a, b) => b.managed.length - a.managed.length || nameOf(a.supervisor).localeCompare(nameOf(b.supervisor)));
  }, [childrenMap, byId, subtreeOf, t]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return teams;
    return teams.filter(team =>
      nameOf(team.supervisor).toLowerCase().includes(q) ||
      team.managed.some(m => nameOf(m.worker).toLowerCase().includes(q))
    );
  }, [teams, search]);

  const supervisorsCount = teams.length;
  const managedTotal     = useMemo(() => teams.reduce((s, tm) => s + tm.direct, 0), [teams]);
  const avgSize          = teams.length ? (managedTotal / teams.length).toFixed(1) : '—';
  const largest          = teams.length ? teams[0].managed.length : '—';

  // Drill-down state
  const current = path.length ? path[path.length - 1] : null;
  const currentManaged = useMemo(() => (current ? subtreeOf(idOf(current)) : []), [current, subtreeOf]);
  const currentDirect  = current ? (childrenMap[idOf(current)]?.length || 0) : 0;
  const currentLevels  = useMemo(() => currentManaged.reduce((mx, m) => Math.max(mx, m.level), 0), [currentManaged]);

  const dialogMembers = useMemo(() => {
    const q = teamSearch.trim().toLowerCase();
    if (!q) return currentManaged;
    return currentManaged.filter(m =>
      nameOf(m.worker).toLowerCase().includes(q) || (positionOf(m.worker) || '').toLowerCase().includes(q)
    );
  }, [currentManaged, teamSearch]);

  const openManager = (team) => { setPath([team.supervisor]); setTeamSearch(''); };
  const drillInto   = (worker) => { setPath(p => [...p, worker]); setTeamSearch(''); };
  const goBack      = () => { setPath(p => p.slice(0, -1)); setTeamSearch(''); };
  const closeDialog = () => { setPath([]); setTeamSearch(''); };

  const selName = current ? nameOf(current) : '';
  const [selFg, selBg] = avatarColors(selName || '—');

  const thSx = { fontWeight: 700, fontSize: '.66rem', color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.3, py: 0.75, bgcolor: 'background.paper', borderBottom: '2px solid', borderColor: 'divider' };

  return (
    <Box sx={{ px: { xs: 1, sm: 2 }, py: 1.5 }}>
      <PageHeader
        title={t('teamManagement.title', 'Menaxhimi i Ekipit')}
        subtitle={t('teamManagement.subtitle', 'Kliko një menaxher për të parë të gjithë ekipin nën menaxhim')}
      />

      {/* KPI row */}
      <Grid container spacing={1.25} sx={{ mb: 1.75 }}>
        {[
          { icon: SupervisorAccountOutlinedIcon, iconBg: alpha(theme.palette.primary.main, 0.12), iconColor: 'primary.main', value: loading ? '—' : supervisorsCount, label: t('teamManagement.supervisors', 'Menaxherë') },
          { icon: PeopleAltOutlinedIcon,          iconBg: alpha(theme.palette.success.main, 0.12), iconColor: 'success.main', value: loading ? '—' : managedTotal, label: t('teamManagement.totalMembers', 'Persona të menaxhuar') },
          { icon: GroupsOutlinedIcon,              iconBg: alpha(theme.palette.info.main, 0.12),    iconColor: 'info.main',    value: loading ? '—' : avgSize, label: t('teamManagement.avgTeamSize', 'Ekip mesatar') },
          { icon: AccountTreeOutlinedIcon,         iconBg: alpha(theme.palette.warning.main, 0.12), iconColor: 'warning.main', value: loading ? '—' : largest, label: t('teamManagement.largestTeam', 'Nën menaxhim (maks)') },
        ].map((card, i) => (
          <Grid item xs={6} sm={3} key={i}><StatCard {...card} loading={loading} /></Grid>
        ))}
      </Grid>

      {/* Search + count */}
      <Stack direction={{ xs: 'column', sm: 'row' }} alignItems={{ sm: 'center' }} spacing={1.25} sx={{ mb: 1.5 }}>
        <TextField
          size="small"
          placeholder={t('teamManagement.searchPlaceholder', 'Kërko menaxher ose person…')}
          value={search}
          onChange={e => setSearch(e.target.value)}
          sx={{ flex: 1, maxWidth: 400 }}
          InputProps={{ startAdornment: (<InputAdornment position="start"><SearchIcon sx={{ fontSize: 18, color: 'text.secondary' }} /></InputAdornment>) }}
        />
        {!loading && (
          <Typography sx={{ fontSize: '.82rem', color: 'text.secondary', whiteSpace: 'nowrap' }}>
            {filtered.length} {t('teamManagement.teamsCount', 'ekipe')}
          </Typography>
        )}
      </Stack>

      {error && <Typography color="error" sx={{ mb: 2 }}>{error}</Typography>}

      {loading && (
        <Stack spacing={1}>
          {[1, 2, 3].map(i => (
            <Paper key={i} variant="outlined" sx={{ borderRadius: 2, p: 1.5 }}>
              <Stack direction="row" spacing={1.5} alignItems="center">
                <Skeleton variant="circular" width={40} height={40} />
                <Box sx={{ flex: 1 }}><Skeleton width="40%" height={18} /><Skeleton width="25%" height={14} sx={{ mt: 0.5 }} /></Box>
                <Skeleton width={90} height={26} sx={{ borderRadius: 4 }} />
              </Stack>
            </Paper>
          ))}
        </Stack>
      )}

      {!loading && !error && filtered.length === 0 && (
        <Paper variant="outlined" sx={{ p: 5, textAlign: 'center', borderRadius: 2 }}>
          <Avatar sx={{ width: 56, height: 56, bgcolor: 'action.hover', color: 'text.disabled', mx: 'auto', mb: 1.5 }}>
            <GroupsOutlinedIcon sx={{ fontSize: 28 }} />
          </Avatar>
          <Typography sx={{ fontWeight: 700, fontSize: '.95rem' }}>{t('teamManagement.noStructureTitle', 'Asnjë strukturë menaxhimi')}</Typography>
          <Typography sx={{ fontSize: '.83rem', color: 'text.secondary', mt: 0.5, maxWidth: 360, mx: 'auto' }}>
            {search ? t('teamManagement.noSearchResult', 'Asnjë përputhje për kërkimin') : t('teamManagement.noStructureHint', 'Cakto mbikëqyrës te punëtorët për të parë ekipet')}
          </Typography>
        </Paper>
      )}

      {/* Manager list — each row opens the professional table dialog */}
      <Stack spacing={1}>
        {filtered.map((team) => {
          const supId  = idOf(team.supervisor);
          const supName = nameOf(team.supervisor);
          const [fg, bg] = avatarColors(supName);
          return (
            <Paper
              key={supId}
              elevation={0}
              onClick={() => openManager(team)}
              sx={{
                border: '1px solid', borderColor: 'divider', borderRadius: 2,
                display: 'flex', alignItems: 'center', gap: 1.25, px: 1.5, py: 1,
                cursor: 'pointer', transition: 'all .15s',
                '&:hover': { borderColor: alpha(fg, 0.55), bgcolor: alpha(fg, 0.04), boxShadow: `0 2px 10px ${alpha(fg, 0.12)}` },
              }}
            >
              <Avatar sx={{ width: 40, height: 40, fontSize: 14, fontWeight: 700, bgcolor: bg, color: fg, flexShrink: 0 }}>
                {initialsOf(supName)}
              </Avatar>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography sx={{ fontSize: '0.9rem', fontWeight: 700, lineHeight: 1.2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {supName}
                </Typography>
                <Typography sx={{ fontSize: '.72rem', color: 'text.secondary', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {[positionOf(team.supervisor), departmentOf(team.supervisor)].filter(Boolean).join(' · ') || '—'}
                </Typography>
              </Box>
              <AvatarGroup
                max={6}
                sx={{ display: { xs: 'none', md: 'flex' }, mr: 0.5, '& .MuiAvatar-root': { width: 27, height: 27, fontSize: 10, fontWeight: 700, border: '2px solid', borderColor: 'background.paper' } }}
              >
                {team.managed.map(({ worker: r }) => {
                  const rn = nameOf(r);
                  const [rfg, rbg] = avatarColors(rn);
                  return <Avatar key={idOf(r)} title={rn} sx={{ bgcolor: rbg, color: rfg }}>{initialsOf(rn)}</Avatar>;
                })}
              </AvatarGroup>
              <Chip
                size="small"
                icon={<AccountTreeOutlinedIcon sx={{ fontSize: 14, color: `${fg} !important` }} />}
                label={`${team.managed.length} ${t('teamManagement.managed', 'nën menaxhim')}`}
                sx={{ fontWeight: 700, fontSize: '.7rem', height: 24, bgcolor: alpha(fg, 0.12), color: fg, border: `1px solid ${alpha(fg, 0.25)}`, '& .MuiChip-label': { px: 0.75 } }}
              />
              {team.managed.length !== team.direct && (
                <Chip size="small" label={`${team.direct} ${t('teamManagement.direct', 'direkt')}`} sx={{ fontWeight: 600, fontSize: '.68rem', height: 24, display: { xs: 'none', md: 'flex' }, bgcolor: 'action.hover', color: 'text.secondary' }} />
              )}
              <Tooltip title={t('teamManagement.openCard', 'Hap profilin')}>
                <IconButton size="small" component={Link} to={`/workerlist/worker/${supId}`} onClick={e => e.stopPropagation()} sx={{ color: 'text.secondary', '&:hover': { color: fg } }}>
                  <OpenInNewIcon sx={{ fontSize: 15 }} />
                </IconButton>
              </Tooltip>
              <KeyboardArrowRightRoundedIcon sx={{ fontSize: 22, color: 'text.disabled' }} />
            </Paper>
          );
        })}
      </Stack>

      {/* ─── Professional table dialog (with drill-down) ─────────────────── */}
      <Dialog open={path.length > 0} onClose={closeDialog} maxWidth="lg" fullWidth
        PaperProps={{ sx: { borderRadius: 3, maxHeight: '92vh' } }}>
        {current && (
          <>
            {/* Header */}
            <Box sx={{ p: 2, pb: 1.5, bgcolor: alpha(selFg, 0.05), borderBottom: '1px solid', borderColor: 'divider' }}>
              {path.length > 1 && (
                <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mb: 1, flexWrap: 'wrap' }}>
                  <IconButton size="small" onClick={goBack} sx={{ mr: 0.25 }}><ArrowBackRoundedIcon sx={{ fontSize: 18 }} /></IconButton>
                  {path.map((node, i) => (
                    <React.Fragment key={idOf(node)}>
                      {i > 0 && <KeyboardArrowRightRoundedIcon sx={{ fontSize: 16, color: 'text.disabled' }} />}
                      <Typography
                        onClick={() => i < path.length - 1 && setPath(p => p.slice(0, i + 1))}
                        sx={{ fontSize: '.72rem', fontWeight: i === path.length - 1 ? 700 : 600, color: i === path.length - 1 ? 'text.primary' : 'primary.main', cursor: i === path.length - 1 ? 'default' : 'pointer', '&:hover': { textDecoration: i === path.length - 1 ? 'none' : 'underline' } }}
                      >
                        {nameOf(node)}
                      </Typography>
                    </React.Fragment>
                  ))}
                </Stack>
              )}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Avatar sx={{ width: 48, height: 48, fontSize: 16, fontWeight: 700, bgcolor: selBg, color: selFg, flexShrink: 0 }}>{initialsOf(selName)}</Avatar>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography sx={{ fontWeight: 800, fontSize: '1rem', lineHeight: 1.25, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{selName}</Typography>
                  <Typography sx={{ fontSize: '.75rem', color: 'text.secondary', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {[positionOf(current), departmentOf(current)].filter(Boolean).join(' · ') || '—'}
                  </Typography>
                </Box>
                <Tooltip title={t('teamManagement.openCard', 'Hap profilin')}>
                  <IconButton size="small" component={Link} to={`/workerlist/worker/${idOf(current)}`} onClick={closeDialog} sx={{ color: 'text.secondary' }}><OpenInNewIcon sx={{ fontSize: 17 }} /></IconButton>
                </Tooltip>
                <IconButton size="small" onClick={closeDialog}><CloseRoundedIcon /></IconButton>
              </Box>
            </Box>

            {/* Stats + search */}
            <Box sx={{ px: 2, pt: 1.5, pb: 1 }}>
              <Stack direction="row" spacing={0.75} sx={{ mb: 1.25, flexWrap: 'wrap', gap: 0.75 }}>
                <Chip size="small" icon={<AccountTreeOutlinedIcon sx={{ fontSize: 14, color: `${selFg} !important` }} />} label={`${currentManaged.length} ${t('teamManagement.managed', 'nën menaxhim')}`} sx={{ fontWeight: 700, fontSize: '.7rem', bgcolor: alpha(selFg, 0.12), color: selFg }} />
                <Chip size="small" label={`${currentDirect} ${t('teamManagement.direct', 'direkt')}`} sx={{ fontWeight: 600, fontSize: '.7rem', bgcolor: 'action.hover', color: 'text.secondary' }} />
                {currentLevels > 1 && <Chip size="small" label={`${currentLevels} ${t('teamManagement.levels', 'nivele')}`} sx={{ fontWeight: 600, fontSize: '.7rem', bgcolor: 'action.hover', color: 'text.secondary' }} />}
              </Stack>
              <TextField
                size="small" fullWidth
                placeholder={t('teamManagement.searchInTeam', 'Kërko brenda ekipit…')}
                value={teamSearch}
                onChange={e => setTeamSearch(e.target.value)}
                InputProps={{ startAdornment: (<InputAdornment position="start"><SearchIcon sx={{ fontSize: 18, color: 'text.secondary' }} /></InputAdornment>) }}
              />
            </Box>

            <Divider />

            {/* Team table */}
            <DialogContent sx={{ p: 0 }}>
              {dialogMembers.length === 0 ? (
                <Stack direction="row" alignItems="center" spacing={1} sx={{ p: 3, color: 'text.disabled', justifyContent: 'center' }}>
                  <PersonOutlineIcon sx={{ fontSize: 18 }} />
                  <Typography sx={{ fontSize: '.83rem' }}>
                    {teamSearch ? t('teamManagement.noSearchResult', 'Asnjë përputhje') : t('teamManagement.noReports', 'Asnjë raport')}
                  </Typography>
                </Stack>
              ) : (
                <TableContainer>
                  <Table size="small" stickyHeader sx={{ '& td': { py: 0.6, borderColor: 'divider' } }}>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={thSx}>{t('teamManagement.colPerson', 'Personi')}</TableCell>
                        <TableCell sx={{ ...thSx, display: { xs: 'none', sm: 'table-cell' } }}>{t('teamManagement.colPosition', 'Pozita')}</TableCell>
                        <TableCell sx={{ ...thSx, display: { xs: 'none', md: 'table-cell' } }}>{t('teamManagement.colHired', 'Punësuar')}</TableCell>
                        <TableCell sx={{ ...thSx, display: { xs: 'none', md: 'table-cell' } }}>{t('teamManagement.colEnds', 'Mbaron')}</TableCell>
                        <TableCell sx={{ ...thSx }} align="center">{t('teamManagement.colLevel', 'Niveli')}</TableCell>
                        <TableCell sx={{ ...thSx }} align="right"> </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {dialogMembers.map(({ worker: r, level }) => {
                        const rid = idOf(r);
                        const rname = nameOf(r);
                        const ractive = r.Active !== false && r.active !== false;
                        const direct1 = level === 1;
                        const drillCount = childrenMap[rid]?.length || 0;
                        const canDrill = drillCount > 0;
                        const [rfg, rbg] = avatarColors(rname);
                        const mgrName = level > 1 ? nameOf(byId[String(r.SupervisorId ?? r.supervisor_id)] || {}) : '';
                        return (
                          <TableRow
                            key={rid}
                            hover
                            onClick={canDrill ? () => drillInto(r) : undefined}
                            sx={{ cursor: canDrill ? 'pointer' : 'default', opacity: ractive ? 1 : 0.5, '&:hover': { bgcolor: alpha(rfg, 0.05) } }}
                          >
                            {/* Person (indented by hierarchy level) */}
                            <TableCell>
                              <Stack direction="row" alignItems="center" spacing={1} sx={{ pl: (level - 1) * 2.5 }}>
                                {level > 1 && (
                                  <Box sx={{ color: 'text.disabled', fontSize: '.9rem', lineHeight: 1, mr: -0.25, flexShrink: 0 }}>└</Box>
                                )}
                                <Avatar sx={{ width: 30, height: 30, fontSize: 11, fontWeight: 700, bgcolor: rbg, color: rfg, flexShrink: 0 }}>{initialsOf(rname)}</Avatar>
                                <Box sx={{ minWidth: 0 }}>
                                  <Typography sx={{ fontSize: '.82rem', fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{rname}</Typography>
                                  {level > 1 && (
                                    <Typography sx={{ fontSize: '.65rem', color: 'text.secondary', whiteSpace: 'nowrap' }}>{t('teamManagement.via', 'nën')} {mgrName}</Typography>
                                  )}
                                </Box>
                              </Stack>
                            </TableCell>
                            {/* Position */}
                            <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' }, fontSize: '.76rem', color: 'text.secondary', maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {positionOf(r) || '—'}
                            </TableCell>
                            {/* Hired */}
                            <TableCell sx={{ display: { xs: 'none', md: 'table-cell' }, fontSize: '.76rem', whiteSpace: 'nowrap' }}>
                              {fmtD(hireOf(r)) || '—'}
                            </TableCell>
                            {/* Ends */}
                            <TableCell sx={{ display: { xs: 'none', md: 'table-cell' }, fontSize: '.76rem', whiteSpace: 'nowrap', color: endOf(r) ? 'error.main' : 'text.disabled', fontWeight: endOf(r) ? 600 : 400 }}>
                              {fmtD(endOf(r)) || '—'}
                            </TableCell>
                            {/* Level */}
                            <TableCell align="center">
                              <Stack direction="row" spacing={0.5} justifyContent="center" alignItems="center">
                                <Chip
                                  size="small"
                                  label={direct1 ? t('teamManagement.direct', 'Direkt') : `N${level}`}
                                  sx={{ height: 18, fontSize: '.6rem', fontWeight: 700, bgcolor: direct1 ? alpha(rfg, 0.14) : 'action.hover', color: direct1 ? rfg : 'text.secondary', '& .MuiChip-label': { px: 0.6 } }}
                                />
                                {canDrill && (
                                  <Chip
                                    size="small"
                                    icon={<AccountTreeOutlinedIcon sx={{ fontSize: 12, color: `${rfg} !important` }} />}
                                    label={drillCount}
                                    sx={{ height: 18, fontSize: '.6rem', fontWeight: 700, bgcolor: alpha(rfg, 0.12), color: rfg, '& .MuiChip-label': { px: 0.4 } }}
                                  />
                                )}
                              </Stack>
                            </TableCell>
                            {/* Actions */}
                            <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                              <Tooltip title={t('teamManagement.openCard', 'Hap profilin')}>
                                <IconButton size="small" component={Link} to={`/workerlist/worker/${rid}`} onClick={e => { e.stopPropagation(); closeDialog(); }} sx={{ color: 'text.disabled', p: 0.4 }}>
                                  <OpenInNewIcon sx={{ fontSize: 15 }} />
                                </IconButton>
                              </Tooltip>
                              {canDrill && (
                                <Tooltip title={t('teamManagement.viewTeam', 'Shiko ekipin')}>
                                  <IconButton size="small" onClick={e => { e.stopPropagation(); drillInto(r); }} sx={{ color: 'text.secondary', p: 0.4 }}>
                                    <KeyboardArrowRightRoundedIcon sx={{ fontSize: 18 }} />
                                  </IconButton>
                                </Tooltip>
                              )}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </DialogContent>
          </>
        )}
      </Dialog>
    </Box>
  );
}
